import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DeepAnalysisResult, CategoryScore, SiteAnalysisResult, NewBonusSection, LegacyBonusSection } from '@/types/analysisTypes';
import { getScoreValue } from './utils';

/**
 * Generate a PDF report from the deep analysis results
 */
export function generatePdfReport(result: DeepAnalysisResult, pageUrl: string): jsPDF {
  // Initialize a new PDF document
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(20);
  doc.setTextColor(44, 62, 80);
  doc.text('Content Analysis Report', 14, 22);
  
  // Add URL
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`URL: ${pageUrl}`, 14, 30);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 35);
  
  // Add a dividing line
  doc.setDrawColor(200, 200, 200);
  doc.line(14, 40, 196, 40);
  
  // Overall score section
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Overall Score', 14, 50);
  
  // Set color based on score
  doc.setFontSize(30);
  if (result.overall_score >= 70) {
    doc.setTextColor(46, 125, 50); // Green for high scores
  } else if (result.overall_score >= 40) {
    doc.setTextColor(255, 153, 0); // Orange for medium scores
  } else {
    doc.setTextColor(220, 53, 69); // Red for low scores
  }
  
  doc.text(`${result.overall_score}/100`, 105, 50);
  
  // Category scores section title
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Category Scores', 14, 65);
  
  // Category scores table
  const categoryHeaders = [['Category', 'Score', 'Description']];
  const categoryRows = Object.entries(result.category_scores).map(([key, value]) => {
    const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    // Get score and description based on value type
    let score: number;
    let description: string;
    
    if (typeof value === 'number') {
      score = value;
      description = '';
    } else {
      score = value.score;
      description = value.description || '';
    }
    
    return [formattedKey, `${score}/20`, description];
  });
  
  autoTable(doc, {
    head: categoryHeaders,
    body: categoryRows,
    startY: 70,
    theme: 'grid',
    headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 'auto' }
    }
  });
  
  // Actionable fixes section title
  let currentY = (doc as any).lastAutoTable.finalY + 15;
  
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Recommended Improvements', 14, currentY);
  
  currentY += 10;
  
  // Actionable fixes table
  if (result.actionable_fixes && result.actionable_fixes.length > 0) {
    const fixesHeaders = [['Current Issue', 'Suggested Fix', 'Rationale']];
    const fixesRows = result.actionable_fixes.map(fix => [
      fix.current_issue,
      fix.suggested_fix,
      fix.rationale
    ]);
    
    autoTable(doc, {
      head: fixesHeaders,
      body: fixesRows,
      startY: currentY,
      theme: 'grid',
      headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'auto' }
      }
    });
    
    currentY = (doc as any).lastAutoTable.finalY + 15;
  } else {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('No improvement suggestions available.', 14, currentY);
    currentY += 10;
  }
  
  // Add new page for additional sections
  doc.addPage();
  currentY = 20;
  
  // Shareability scores section
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Audience Shareability Analysis', 14, currentY);
  
  currentY += 10;
  
  const shareabilityHeaders = [['Audience', 'Score', 'Explanation', 'Improvement']];
  const shareabilityRows = Object.entries(result.shareability_scores).map(([key, value]) => [
    key.charAt(0).toUpperCase() + key.slice(1),
    `${typeof value === 'number' ? value : value.score}/100`,
    typeof value === 'number' ? 'N/A' : value.explanation,
    typeof value === 'number' ? 'N/A' : value.improvement
  ]);
  
  autoTable(doc, {
    head: shareabilityHeaders,
    body: shareabilityRows,
    startY: currentY,
    theme: 'grid',
    headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
    styles: { fontSize: 10 }
  });
  
  currentY = (doc as any).lastAutoTable.finalY + 15;
  
  // Bonus section title
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Expert Creative Insights', 14, currentY);
  
  currentY += 10;
  
  // Type guard to check if we have new or old format
  const isNewFormat = (section: any): section is NewBonusSection => {
    return section.expert_insight && 'title' in section.expert_insight;
  };
  
  // Bonus section table - handle both old and new formats
  if (isNewFormat(result.bonus_section)) {
    // New format - use titles and other new fields
    const bonusSection = result.bonus_section as NewBonusSection;
    const bonusHeaders = [['Insight Type', 'Title', 'Content']];
    const bonusRows = Object.entries(bonusSection)
      .filter(([key]) => ['expert_insight', 'unexpected_analogy', 'challenged_assumption', 'insider_tip'].includes(key))
      .map(([key, value]) => {
        const insightType = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        let title = '';
        let content = '';
        
        // Create a condensed content summary based on the type
        switch (key) {
          case 'expert_insight': {
            const insight = value as NewBonusSection['expert_insight'];
            title = insight.title || '';
            content = `${insight.intro || ''}\n\nKey takeaways: ${(insight.key_takeaways || []).join(', ')}`;
            break;
          }
          case 'unexpected_analogy': {
            const analogy = value as NewBonusSection['unexpected_analogy'];
            title = analogy.title || '';
            content = `${analogy.intro || ''}\n\nKey points: ${(analogy.key_points || []).join(', ')}`;
            break;
          }
          case 'challenged_assumption': {
            const assumption = value as NewBonusSection['challenged_assumption'];
            title = assumption.title || '';
            content = `${assumption.intro || ''}\n\nKey reasons: ${(assumption.key_reasons || []).join(', ')}`;
            break;
          }
          case 'insider_tip': {
            const tip = value as NewBonusSection['insider_tip'];
            title = tip.title || '';
            content = `${tip.intro || ''}\n\nStep by step: ${(tip.step_by_step || []).join(', ')}`;
            break;
          }
        }
        
        return [insightType, title, content];
      });
    
    autoTable(doc, {
      head: bonusHeaders,
      body: bonusRows,
      startY: currentY,
      theme: 'grid',
      headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
      styles: { fontSize: 10 }
    });
  } else {
    // Old format - use question/example fields
    const legacySection = result.bonus_section as LegacyBonusSection;
    const bonusHeaders = [['Insight Type', 'Question', 'Example/Answer']];
    const bonusRows = Object.entries(legacySection)
      .filter(([key]) => ['expert_insight', 'unexpected_analogy', 'challenged_assumption', 'insider_tip'].includes(key))
      .map(([key, value]) => {
        const insightType = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        // Safely access question/example fields
        if (value && typeof value === 'object') {
          const legacyItem = value as LegacyBonusSection[typeof key];
          if (typeof legacyItem !== 'string') {
            const question = legacyItem.question || '';
            const example = legacyItem.example || '';
            return [insightType, question, example];
          }
        }
        return [insightType, '', ''];
      });
    
    autoTable(doc, {
      head: bonusHeaders,
      body: bonusRows,
      startY: currentY,
      theme: 'grid',
      headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
      styles: { fontSize: 10 }
    });
  }
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(
      `Generated by SEO Content Analyzer | Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  return doc;
}

/**
 * Generate a PDF report for site analysis
 */
export function generateSitePdfReport(result: SiteAnalysisResult, siteName: string): jsPDF {
  // Initialize a new PDF document
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(20);
  doc.setTextColor(44, 62, 80);
  doc.text('Site Analysis Report', 14, 22);
  
  // Add Site Name
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Site: ${siteName}`, 14, 30);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 35);
  
  // Add a dividing line
  doc.setDrawColor(200, 200, 200);
  doc.line(14, 40, 196, 40);
  
  // Overall score section
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Overall Site Score', 14, 50);
  
  // Set color based on score
  doc.setFontSize(30);
  if (result.siteOverallScore >= 70) {
    doc.setTextColor(46, 125, 50); // Green for high scores
  } else if (result.siteOverallScore >= 40) {
    doc.setTextColor(255, 153, 0); // Orange for medium scores
  } else {
    doc.setTextColor(220, 53, 69); // Red for low scores
  }
  
  doc.text(`${Math.round(result.siteOverallScore)}/100`, 105, 50);
  
  // Category scores section title
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Site Category Scores', 14, 65);
  
  // Site category scores table
  const categoryHeaders = [['Category', 'Score']];
  const categoryRows = Object.entries(result.siteCategoryScores).map(([key, score]) => {
    const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return [formattedKey, `${Math.round(score)}/100`];
  });
  
  autoTable(doc, {
    head: categoryHeaders,
    body: categoryRows,
    startY: 70,
    theme: 'grid',
    headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 40, halign: 'center' }
    }
  });
  
  // Pages summary section
  let currentY = (doc as any).lastAutoTable.finalY + 15;
  
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Analyzed Pages', 14, currentY);
  
  currentY += 10;
  
  // Pages table
  const pagesHeaders = [['Page URL', 'Score']];
  const pagesRows = Object.entries(result.pageResults)
    .sort((a, b) => b[1].overall_score - a[1].overall_score)
    .map(([url, analysis]) => {
      // Truncate long URLs for better readability
      const displayUrl = url.length > 60 
        ? url.substring(0, 57) + '...' 
        : url;
      return [displayUrl, `${Math.round(analysis.overall_score)}/100`];
    });
  
  autoTable(doc, {
    head: pagesHeaders,
    body: pagesRows,
    startY: currentY,
    theme: 'grid',
    headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 30, halign: 'center' }
    }
  });
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(
      `Generated by SEO Content Analyzer | Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  return doc;
}

/**
 * Export page recommendations to PDF
 */
export function exportPageRecommendationsToPDF(result: DeepAnalysisResult, pageUrl: string): void {
  const doc = generatePdfReport(result, pageUrl);
  doc.save(`content-analysis-${new Date().getTime()}.pdf`);
}

/**
 * Export site analysis to PDF
 */
export function exportSiteAnalysisToPDF(result: SiteAnalysisResult, siteName: string): void {
  const doc = generateSitePdfReport(result, siteName);
  doc.save(`site-analysis-${new Date().getTime()}.pdf`);
}
